import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookShare } from "@shared/models/analytics/planbook"
import type { PreparedAction } from "../../action-doc-builder"
import { assign, partial } from "lodash-es"
import type { Narrative } from "../create-narrative"
import createNarrative from "../create-narrative"
import defaultPlanbookContext from "../planbook/default-planbook-context"
import defaultPlanbookModels from "../planbook/default-planbook-models"

interface Payload {
  userId: string
  planbookId: string
  planbookTitle: string
  copied: "link" | "qr_code"
  perspective: string
  url: string
  shareScope: "planbook" | "current_view"
}

export default function copyPlanbookLinkAction(payload: Payload): PreparedAction {
  return {
    name: "COPY_PLANBOOK_LINK",
    payload: payload,
    patches: null,
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultPlanbookModels(payload, findFn, userId, payload.planbookId)
  let context = defaultPlanbookContext(payload, models)
  let newContext = assign({}, context, {
    url: payload.url,
    perspective: payload.perspective,
    shareScope: payload.shareScope,
  })

  let primaryObjectSpecifier
  if (payload.perspective === "standards-tracker") {
    primaryObjectSpecifier = "standards_tracker"
  } else {
    primaryObjectSpecifier = payload.perspective
  }

  let newEvent: PlanbookShare = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    primary_object_type: "planbook",
    primary_possessive: "owns",
    primary_object_id_and_ancestor_ids: [payload.planbookId],
    primary_verb: "share",
    primary_object_specifier: primaryObjectSpecifier,
    adverbial_verb: payload.copied === "link" ? "copying" : "downloading",
    adverbial_object_type: "link",
    adverbial_object_specifier: payload.copied === "qr_code" ? "qr_code" : null,
  }

  const narrativeByType: Record<
    Payload["copied"],
    { title: string; titleForAnalytics: string; description: string }
  > = {
    qr_code: {
      title: `Downloaded QR code from the ${payload.perspective} view.`,
      titleForAnalytics: "Planbook - Download QR Code",
      description: `Downloading QR code for the ${payload.perspective} view.`,
    },
    link: {
      title: `Copied link from the ${payload.perspective} view.`,
      titleForAnalytics: "Planbook - Copy Link",
      description: `Copying link to the ${payload.perspective} view.`,
    },
  }

  return createNarrative({
    ...narrativeByType[payload.copied],
    context: newContext,
    newAnalyticsEvents: [newEvent],
  })
}
